<template>
    <div>
        <!-- The bulk mark, over the checkbox selection. It used to be
             unreachable here: the computed already resolved to a boolean and
             the template compared it against the string 'manual', which is
             never true. Project mode is the default tree, so the manual mode
             had no surface at all outside by-date company-wide. -->
        <div v-if="manual_mode">
            <div class="my-2" v-if="selected_actuals.length == 0"><br /></div>
            <div v-else class="d-flex align-center ga-2 my-2">
                <span class="text-caption text-medium-emphasis">
                    {{ t("kit_selected_count").replace("{n}", selected_actuals.length) }}
                </span>
                <v-spacer />
                <ButtonSetAsReady :actuals="selected_actuals" />
            </div>
        </div>
        <ButtonSelectionFromFinalReport @selected="selected_actuals = $event" />
        <v-treeview color="accent" open-all class="mb-3" item-disabled="locked" density="compact" selectable
            select-strategy="auto" item-value="id" v-model:selected="selected_actuals" :items="actuals_tree">
            <template v-slot:label="{ item }">
                <span class="text-grey" v-if="item.ready || item.kitted"> {{ item.title }} </span>
                <span v-else> {{ item.title }} </span>
            </template>
            <template v-slot:append="{ item }">
                <v-chip variant="outlined" size="x-small" v-if="item.ready" color="green">
                    {{ t("ready") }}
                </v-chip>
                <!-- Kit progress on every panel row, so the operator can see
                     from the list which kits are complete without opening
                     anything. Read-only unless the company is on manual. -->
                <template v-else-if="kit_on && item.kit">
                    <v-chip variant="outlined" size="x-small" :color="item.kit.color" class="pit-mono">
                        {{ item.kit.text }}
                    </v-chip>
                    <v-btn v-if="manual_mode && !item.kitted" icon="mdi-check" size="x-small" variant="text"
                        color="success" class="ml-1" :title="t('kit_mark')" @click.stop="markOne(item)" />
                </template>
            </template>
        </v-treeview>
    </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import _ from "lodash"
import ButtonSetAsReady from "../../components/left_menu/ButtonSetAsReady.vue";
import { nameSort } from "/src/utils/basics.js"
import { useConstructiblesStore } from "@/stores/constructibles";
import { useSelectedLogisticsStore } from "@/stores/selected_logistics";
import { useSelectedStore } from "@/stores/selected";
import { useSessionStore } from "@/stores/session";
import ButtonSelectionFromFinalReport from "../logistics_viewer/ButtonSelectionFromFinalReport.vue"
import { kitFraction, isKitted } from "/src/utils/kits.js"
import { usePitStoreBasics } from "@/stores/app_common"
import { markKits } from "/src/utils/mark_kits.js"
import { projectPhases } from "@/utils/phase_keys";

// Stores
const constructibles_store = useConstructiblesStore();
const selected_logistics_store = useSelectedLogisticsStore();
const selected_store = useSelectedStore();
const session_store = useSessionStore();
const { t } = usePitStoreBasics();

// Reactive variables
const selected_actuals = ref([]);
const by_priority = ref(true);

// Computed properties
const project = computed(() => selected_store.selected_project || '');
const batches = computed(() => constructibles_store.batches);
const actuals = computed(() => constructibles_store.allActuals);
const manual_mode = computed(() => session_store.kit_mode === 'manual');
const kit_on = computed(() => session_store.kit_tracking_on);

const actuals_tree = computed(() => {
    let actuals_tree = []

    // ITERATE PHASES, in the project's order
    if (project.value) {
        projectPhases(project.value).forEach((phase) => {

            // ITERATE PHASE BATCHES
            let children_batches = []
            batches.value.filter((b) => b.phase_uuid == phase.uuid).forEach((b) => {

                // GENERATE BATCH CHILDREN
                let children_actuals = []
                actuals.value
                    .filter((actual) => actual.batch == b.uuid)
                    .forEach((actual) => {
                        children_actuals.push({
                            kitted: isKitted(actual),
                            kit: kit_on.value ? kitFraction(actual, t) : null,
                            ready: actual.status == "ready",
                            id: actual.uuid,
                            design_name: getDesignName(actual.design),
                            priority: actual.priority,
                            title: `${getDesignName(actual.design)} [${actual.uuid.substring(0, 8)}]`,
                        })
                    })

                // NOW SORT CHILDREN
                let sorted_children
                if (by_priority) {
                    sorted_children = _.orderBy(
                        children_actuals,
                        "priority"
                    )
                } else {
                    sorted_children = children_actuals.sort((a, b) =>
                        nameSort(a.design_name, b.design_name)
                    )
                }

                // ADD BATCH TO PHASE CHILDREN
                children_batches.push({
                    id: b.uuid,
                    title: b.name,
                    locked: sorted_children.length == 0,
                    children: sorted_children,
                })
            })

            // ADD PHASE TO TREE
            actuals_tree.push({
                id: phase.uuid,
                title: phase.name,
                locked: children_batches.length == 0,
                children: children_batches,
            })
        })
    }

    return actuals_tree
});

watch(selected_actuals, (sa) => {
    selected_logistics_store.setSelectedActuals(sa);
});

// Methods
function getDesignName(duuid) {
    return constructibles_store.designNameByUuid(duuid);
}

/** One tap on a row: the per-panel half of the manual mode. */
function markOne(item) {
    return markKits([item.id], true, t);
}

</script>